// @flow
import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
// Analytics
import * as Analytics from '../../../common/analytics';
// Components
import { Button, ConfirmationModal, RadioButton } from '../../../common';
import CheckoutErrors from '../CheckoutErrors';
import PanelCard from '../PanelCard';
import AddressForm from './AddressForm';
import AddressPicker from './AddressPicker';
// Types
import type { UserAddress } from '../../../user';
import type { DeliveryOptions, OrderAddress, TakeoutAddress, ShippingRate } from '../../types';
import type { Address } from '../../../types/Address';
// Styles
import './AddressPane.css';
// Assets
import plusIcon from '../../images/plus.svg';
import Takeout from '../delivery/Takeout';

export type State = {
  editing: number | 'new' | false,
  address: ?UserAddress,
};

export type Props = {
  errors: string[],
  active: boolean,
  selected: ?OrderAddress,
  loading: boolean,
  onSubmit: (address: UserAddress) => void,
  dismissErrors: () => void,
  hasOnlyGiftCards: boolean,
  orderNumber: string,
  // Address Connector
  addresses: UserAddress[], // From connector
  addressesLoading: boolean, // From connector
  editingLoading: boolean, // From connector
  addingLoading: boolean, // From connector
  deletingLoading: (id: number) => boolean, // From connector
  onNew: (address: UserAddress) => Promise<?Error>, // From connector
  onEdit: (address: UserAddress) => Promise<?Error>, // From connector
  onDelete: (id: number) => void, // From connector
  transitionToAddress: () => void,
  isCompleted: boolean,
  takeoutAddress: TakeoutAddress[],
  shippingRates: ShippingRate[],
  deliveryOptionsCount: DeliveryOptions,
};

const defaultAddress: UserAddress = {
  id: null,
  baseAddressId: null,
  receiverName: '',
  addressLine1: '',
  addressLine2: '',
  neighborhood: '',
  latitude: null,
  longitude: null,
  city: '',
  zipcode: '',
  additionalInformation: '',
  reference: '',
  telephone: '',
  cnpj: '',
  company: '',
  residenceType: '',
  residenceNumber: '',
  residenceData: '',
  deliveryCost: '',
};

const AddressPane = (props: Props) => {
  const addressLengthRef = useRef();
  const isActiveRef = useRef();
  const [isEditing, setIsEditing] = useState(false);
  const [address, setAddress] = useState(defaultAddress);
  const [showModal, setShowModal] = useState(false);
  const [addressOption, setAddressOption] = useState('empty');

  const takeoutAddress = props.takeoutAddress;
  const bigDeliveryAddress: TakeoutAddress = {
    id: takeoutAddress?.id,
    receiverName: takeoutAddress?.firstname,
    addressLine1: takeoutAddress?.address1,
    addressLine2: takeoutAddress?.address2,
    city: takeoutAddress?.city,
    zipcode: takeoutAddress?.zipcode,
    telephone: takeoutAddress?.phone,
  };

  const handleAddresses = () => {
    const hasAnAddress = props.addresses.length > 0;
    if (!hasAnAddress) {
      setIsEditing(true);
      setAddress(defaultAddress);
      return;
    }
    setAddress(_.maxBy(props.addresses, 'id'));
  };

  const hasTakeoutShippingRate = props.deliveryOptionsCount.takeout > 0;

  const hasShippingRatesWithoutTakeout =
    props.deliveryOptionsCount.padrao > 0 ||
    props.deliveryOptionsCount.other > 0;

  const handleAddressOption = () => {
    if (props.hasOnlyGiftCards) {
      submitAddress()
      return
    }

    (hasShippingRatesWithoutTakeout &&
      setAddressOption('scheduled_delivery')) ||
      (hasTakeoutShippingRate && setAddressOption('takeout'));
  };

  useEffect(() => {
    if(!props.orderNumber) return;

    isActiveRef.current = props.active;
    addressLengthRef.current = props.addresses.length;
    handleAddresses();
    handleAddressOption();
    // eslint-disable-next-line
  }, [props.orderNumber, props.deliveryOptionsCount]);

  useEffect(() => {
    if (
      addressLengthRef.current !== props.addresses.length ||
      (isActiveRef.current && !props.active && props.addresses.length > 0)
    ) {
      isActiveRef.current = props.active;
      addressLengthRef.current = props.addresses.length;
      const newestAddress = _.maxBy(props.addresses, 'id');
      setAddress(newestAddress);
      setIsEditing(false);
    }
    // eslint-disable-next-line
  }, [props.addresses?.length]);

  const newAddress = () => {
    setIsEditing(prevState => !prevState);
    setAddress(defaultAddress);
  };

  const pickAddress = (address: UserAddress) => {
    if (isEditing) {
      setIsEditing(false);
    }
    setAddress(address);
  };

  const editAddress = (address: UserAddress) => {
    setIsEditing(true);
    setAddress(address);
  };

  const updateAddress = async (address: UserAddress) => {
    const error = await props.onEdit(address);
    if (error) return error;

    setAddress(address);
    setIsEditing(false);
  };

  const deleteAddress = (address: UserAddress) => {
    Analytics.logDeleteAddress(address);
    // Dispatch the action that deletes the address
    props.onDelete(address.id);
  };

  const submitAddress = () => {
    const addressOptionMap = {
      empty: bigDeliveryAddress,
      scheduled_delivery: address,
      takeout: bigDeliveryAddress,
    };
    
    props.onSubmit(addressOptionMap[addressOption]);
  };

  if (props.hasOnlyGiftCards) {
    return (
      null
    );
  }


  if (addressOption === 'empty')
    return (
      <PanelCard
        isOpen={props.active}
        handleClick={props.transitionToAddress}
        title="Endereço de entrega"
        number={1}
        isCompleted={props.isCompleted}
        isLoading={props.loading}
      >
        <h3 className="delivery-pane-title">
          Sem horários de entrega disponíveis.
        </h3>
      </PanelCard>
    );

  return (
    <PanelCard
      isOpen={props.active}
      handleClick={props.transitionToAddress}
      title="Endereço de entrega"
      number={1}
      isCompleted={props.isCompleted}
      isLoading={props.loading}
    >
      {props.active && (
        <div>
          <div className="address-pane-question-container">
            {!props.hasOnlyGiftCards && hasTakeoutShippingRate && (
              <>
                <RadioButton
                  text={'Retirada no BigDelivery'}
                  isSelected={addressOption === 'takeout'}
                  onSelect={() => setAddressOption('takeout')}
                />
                <div className="address-pane-question-text">
                  Retire seu pedido no centro de distribuição mais próximo a
                  você
                  <br /> e Economize na Taxa de Entrega!
                </div>
              </>
            )}
            {!props.hasOnlyGiftCards && hasShippingRatesWithoutTakeout && (
              <>
                <RadioButton
                  text={'Receber no meu endereço'}
                  isSelected={addressOption === 'scheduled_delivery'}
                  onSelect={() => setAddressOption('scheduled_delivery')}
                />
                <div className="address-pane-question-text">
                  Receba seu pedido no conforto da sua casa ou onde você
                  estiver.
                </div>
              </>
            )}
          </div>
          {addressOption === 'scheduled_delivery' && (
            <div>
              <CheckoutErrors
                errors={props.errors}
                dismiss={props.dismissErrors}
                showErrorFields={true}
              />
              <p>
                {props.addresses.length > 0
                  ? 'Selecione um endereço existente ou clique em "adicionar novo endereço" e "Salvar". Depois, clique "confirmar entrega".'
                  : 'Preencha o endereço, salve e depois clique "confirmar endereço".'}
              </p>
              {isEditing && (
                <AddressForm
                  address={address}
                  loading={props.addingLoading}
                  onCancel={() => {
                    setIsEditing(prevValue => !prevValue);
                  }}
                  onSubmitAddress={(addressValues: Address) => {
                    if (addressValues.id) {
                      return updateAddress(addressValues);
                    }
                    return props.onNew(addressValues);
                  }}
                  active={isEditing}
                />
              )}
              {!isEditing && (
                <>
                  <AddressPicker
                    addresses={props.addresses}
                    selected={address}
                    loading={props.loading}
                    editing={isEditing}
                    pickAddress={pickAddress}
                    updateAddress={updateAddress}
                    editAddress={editAddress}
                    deleteAddress={address => {
                      setShowModal(true);
                      setAddress(address);
                    }}
                    editingLoading={props.editingLoading}
                    deletingLoading={props.deletingLoading}
                    addressesLoading={props.addressesLoading}
                  />

                  <button
                    className="address-pane-checkout-continue-button"
                    onClick={newAddress}
                    disabled={isEditing}
                  >
                    <img src={plusIcon} alt="Ícone de mais" />
                    Criar novo endereço
                  </button>

                  <Button
                    onClick={() => {
                      submitAddress();
                      Analytics.logConfirmedAddress();
                    }}
                    buttonType="button-primary"
                    disabled={!address || !address.id}
                    text="Confirmar Endereço"
                    loading={props.loading}
                  />
                </>
              )}

              <ConfirmationModal
                isOpen={showModal}
                text="Gostaria de excluir esse endereço?"
                onClose={() => {
                  setShowModal(false);
                }}
                onConfirm={() => {
                  setShowModal(false);
                  deleteAddress(address);
                }}
              />
            </div>
          )}
          {addressOption === 'takeout' && (
            <div>
              <Takeout />
              <Button
                onClick={() => {
                  submitAddress();
                  Analytics.logConfirmedAddress();
                }}
                buttonType="button-primary"
                disabled={
                  (!address || !address.id) && addressOption !== 'takeout'
                }
                text="Confirmar Retirada"
                loading={props.loading}
              />
            </div>
          )}
        </div>
      )}
    </PanelCard>
  );
};

export default AddressPane;
